import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Trash2, Loader2, Pencil } from "lucide-react";

const NONE = "__none__";

const outline = (text) => (text || "")
  .split("\n")
  .map((l) => l.replace(/^[#>\-*•\d.\)\s]+/, "").trim())
  .filter(Boolean)
  .slice(0, 10);

// Personal + organization custom-template picker (Reports page + Team compile panel).
// Personal templates are strictly per-user; org templates are read-only for members here
// (admins manage them on the Organization → Templates tab). All enforced server-side.
export const CustomTemplatePanel = ({ api, onChange }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(NONE);
  const [source, setSource] = useState("describe");
  const [describeText, setDescribeText] = useState("");
  const [uploadText, setUploadText] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [save, setSave] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);   // {id, name, structure_text}
  const [savingEdit, setSavingEdit] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const res = await api.get("/custom-templates"); setTemplates(res.data || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedId !== NONE && !templates.find((x) => x.id === selectedId)) setSelectedId(NONE);
  }, [templates, selectedId]);

  useEffect(() => {
    if (selectedId !== NONE) {
      onChange({ custom_template_id: selectedId, custom_template: null, save_template: false, template_name: null, custom_template_source: "describe" });
    } else {
      const text = source === "upload" ? uploadText : describeText;
      onChange({
        custom_template_id: null, custom_template: text || null, custom_template_source: source,
        save_template: !!(save && name.trim() && text.trim()), template_name: name.trim() || null,
      });
    }
  }, [selectedId, source, describeText, uploadText, save, name]); // eslint-disable-line react-hooks/exhaustive-deps

  const personal = templates.filter((x) => x.scope !== "org");
  const org = templates.filter((x) => x.scope === "org");
  const selected = templates.find((x) => x.id === selectedId);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".docx") && !lower.endsWith(".pdf")) { toast.error(t("customTpl.badType")); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/custom-templates/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadText(res.data.structure_text || ""); setUploadName(file.name); setSource("upload"); setSelectedId(NONE);
      toast.success(t("customTpl.extracted", { name: file.name }));
    } catch (err) { toast.error(err?.response?.data?.detail || t("customTpl.extractFailed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id, e) => {
    e?.stopPropagation?.(); e?.preventDefault?.();
    if (!window.confirm(t("customTpl.confirmDelete"))) return;
    setSelectedId(NONE);
    try { await api.delete(`/custom-templates/${id}`); setTemplates((a) => a.filter((x) => x.id !== id)); toast.success(t("customTpl.deleted")); }
    catch (err) { toast.error(t("customTpl.deleteFailed")); }
  };

  const openEdit = (tpl, e) => { e?.stopPropagation?.(); e?.preventDefault?.(); setEditing({ id: tpl.id, name: tpl.name, structure_text: tpl.structure_text || "" }); };
  const saveEdit = async () => {
    if (!editing.name.trim()) { toast.error(t("customTpl.nameRequired")); return; }
    setSavingEdit(true);
    try {
      await api.put(`/custom-templates/${editing.id}`, { name: editing.name.trim(), structure_text: editing.structure_text });
      toast.success(t("customTpl.updated")); setEditing(null); await load();
    } catch (err) { toast.error(t("customTpl.updateFailed")); }
    finally { setSavingEdit(false); }
  };

  const Row = ({ tpl }) => <span>{tpl.name}</span>;

  return (
    <div className="mt-3 rounded-lg border border-[#E4E4EC] bg-[#FAFAFC] p-3 space-y-3" data-testid="custom-template-panel">
      {templates.length > 0 && (
        <div>
          <Label className="text-xs">{t("customTpl.savedLabel")}</Label>
          <Select value={selectedId} onValueChange={(v) => { setSelectedId(v); if (v !== NONE) setSource("describe"); }}>
            <SelectTrigger className="mt-1 h-9" data-testid="ct-saved-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t("customTpl.savedNone")}</SelectItem>
              {personal.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{t("customTpl.groupPersonal")}</SelectLabel>
                  {personal.map((tpl) => <SelectItem key={tpl.id} value={tpl.id}><Row tpl={tpl} /></SelectItem>)}
                </SelectGroup>
              )}
              {org.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{t("customTpl.groupOrg")}</SelectLabel>
                  {org.map((tpl) => <SelectItem key={tpl.id} value={tpl.id} data-testid={`ct-org-${tpl.id}`}><Row tpl={tpl} /></SelectItem>)}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          {selected && selected.scope === "personal" && selected.can_edit && (
            <div className="mt-1.5 flex items-center gap-3">
              <button type="button" onClick={() => openEdit(selected)} className="inline-flex items-center gap-1 text-[11px] text-slatebody hover:text-navy" data-testid={`ct-edit-${selected.id}`}>
                <Pencil className="w-3.5 h-3.5" /> {t("customTpl.edit")}
              </button>
              <button type="button" onClick={() => del(selected.id)} className="inline-flex items-center gap-1 text-[11px] text-slatebody hover:text-red-600" data-testid={`ct-delete-${selected.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> {t("customTpl.delete")}
              </button>
            </div>
          )}
        </div>
      )}

      {selectedId === NONE && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".docx,.pdf" onChange={onUpload} className="hidden" data-testid="ct-upload-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-navy border border-[#C5C5D0] rounded-md px-2.5 py-1.5 hover:bg-[#F0F0F5] disabled:opacity-60" data-testid="ct-upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {t("customTpl.uploadBtn")}
            </button>
            {source === "upload" && uploadName && <span className="text-xs text-emerald-600" data-testid="ct-upload-name">{uploadName}</span>}
          </div>
          <div>
            <Label className="text-xs">{t("customTpl.describeLabel")}</Label>
            <textarea value={describeText} onChange={(e) => { setDescribeText(e.target.value); setSource("describe"); }} onFocus={() => setSource("describe")}
              rows={3} placeholder={t("customTpl.describePlaceholder")}
              className="mt-1 w-full text-sm rounded-md border border-[#C5C5D0] p-2 focus:outline-none focus:ring-1 focus:ring-navy" data-testid="ct-describe" />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-slatebody cursor-pointer">
              <Checkbox checked={save} onCheckedChange={(v) => setSave(v === true)} data-testid="ct-save-checkbox" />
              {t("customTpl.saveForLater")}
            </label>
            {save && <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("customTpl.namePlaceholder")} className="h-9 text-sm" data-testid="ct-save-name" />}
          </div>
        </div>
      )}

      {selected && outline(selected.structure_text).length > 0 && (
        <div className="rounded-md border border-[#E4E4EC] bg-white p-2.5" data-testid="ct-preview">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slatebody mb-1">{t("customTpl.previewLabel")}</p>
          <ul className="list-disc pl-4 space-y-0.5 text-xs text-navy">
            {outline(selected.structure_text).map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="ct-edit-dialog">
          <DialogHeader><DialogTitle>{t("customTpl.editTitle")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t("customTpl.namePlaceholder")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-9 text-sm mt-1" data-testid="ct-edit-name" />
              </div>
              <div>
                <Label className="text-xs">{t("customTpl.describeLabel")}</Label>
                <textarea value={editing.structure_text} onChange={(e) => setEditing({ ...editing, structure_text: e.target.value })} rows={6}
                  className="mt-1 w-full text-sm rounded-md border border-[#C5C5D0] p-2 focus:outline-none focus:ring-1 focus:ring-navy" data-testid="ct-edit-structure" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="ct-edit-cancel">{t("common.cancel")}</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="ct-edit-save">{savingEdit ? t("customTpl.saving") : t("customTpl.saveEdit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomTemplatePanel;
